import {
  RUTERO_VERTICALS,
  RuteroVertical,
  VOUCHER_NOT_READY_REASONS,
  VoucherNotReadyReason,
  VoucherTextLanguage,
} from '@bandido/shared/types';

import {
  voucherReasonKey,
  voucherUnsupportedAlternativeKey,
} from '@frontend/features/travel-services/voucherAction';

import { VOUCHER_IDENTITY_FIELDS, VoucherIdentityField } from './voucherIdentity';
import { VoucherTextsDraft, voucherTextsAll } from './voucherTexts';

const KEY = 'vouchers.config';

/**
 * LA LÓGICA de la vista previa, fuera del componente y sin DOM: qué cuerpo se
 * pide, cuándo NO se pide, cómo se sabe si una respuesta sigue siendo la que
 * la pantalla está esperando, y qué se muestra cuando no hay PDF.
 *
 * La vista previa se dispara sola con cada tecla, así que todo lo de acá existe
 * para que no dibuje algo que no corresponde: un PDF de un pedido viejo, de
 * otro vertical, o de un texto que el asesor ya cambió.
 */

/** Los verticales que se pueden elegir. La lista es la de Rutero, no una copia. */
export const VOUCHER_PREVIEW_VERTICALS = RUTERO_VERTICALS;

/** Un bloque tal como viaja a la vista previa, de cualquier alcance. */
export interface VoucherPreviewBlock {
  scope: string;
  key: string;
  lang: string;
  body: string;
  mode?: string;
}

/** El cuerpo del `POST /vouchers/preview`. */
export interface VoucherPreviewBody {
  agencyId: number;
  vertical: RuteroVertical;
  lang: VoucherTextLanguage;
  /** El proveedor con el que se dibuja el ejemplo; sin él, el del ejemplo del vertical. */
  provider?: string;
  blocks: VoucherPreviewBlock[];
}

/**
 * Lo que devuelve el endpoint, ya normalizado: el PDF, o por qué todavía no se
 * puede dibujar. Los motivos son los MISMOS que los del documento real
 * (`VoucherNotReadyReason`), para no aprender un segundo vocabulario.
 */
export type VoucherPreviewResult =
  | { status: 'ready'; blob: Blob }
  | { status: 'unsupported' }
  | { status: 'not_ready'; reason: string };

/**
 * EL CUERPO: TODO lo que el documento real imprimiría, dibujado con el
 * BORRADOR.
 *
 * La vista previa DIBUJA con lo que recibe y con nada más, así que viajan
 * todos los bloques con texto y no solo los cambiados. Los tres alcances salen
 * del borrador —que arranca con todo lo guardado, proveedores incluidos—: vaciar
 * una caja tiene que quitar ese texto del dibujo, y elegir "Sustituye" tiene que
 * sacar el del tipo de producto (`resolveVoucherTexts` lee el modo del bloque).
 *
 * También es la diferencia con el guardado, y por el motivo contrario: el `PUT`
 * toca los bloques que nombra, así que manda solo lo cambiado.
 */
export function voucherPreviewBody(params: {
  agencyId: number;
  vertical: RuteroVertical;
  lang: VoucherTextLanguage;
  texts: VoucherTextsDraft;
  /** El proveedor de la fila abierta: su texto solo se imprime en SUS reservas. */
  provider?: string;
}): VoucherPreviewBody {
  return {
    agencyId: params.agencyId,
    vertical: params.vertical,
    lang: params.lang,
    ...(params.provider ? { provider: params.provider } : {}),
    blocks: voucherTextsAll(params.texts),
  };
}

/**
 * LO QUE LA PREVIEW DIBUJA DE LA IDENTIDAD y que NO viaja en el cuerpo: el
 * endpoint la lee GUARDADA de la agencia.
 *
 * Existe porque el pedido tiene que cambiar cuando cambia: guardar la dirección
 * o subir el logo no toca ni un texto, así que sin esto la vista previa seguía
 * mostrando el documento anterior —o diciendo "subí el logo" con el logo ya
 * subido— hasta que el asesor escribiera cualquier cosa. El documento imprime
 * los DOS logos, el color y los siete datos del pie (`voucherBrandOf`), así que
 * la huella los cubre a todos.
 */
export function voucherIdentityStamp(
  identity:
    | (Partial<Record<VoucherIdentityField, string>> & {
        name?: string;
        headerColor?: string;
        logo?: { id: number } | null;
        logoNegative?: { id: number } | null;
      })
    | undefined,
): string {
  // Los campos se recorren de la MISMA lista que dibuja el formulario: uno
  // nuevo entra solo. Con una lista escrita a mano acá, el campo agregado
  // mañana se imprime en el documento y la vista previa no se entera.
  const campos = [...VOUCHER_IDENTITY_FIELDS, 'headerColor'] as const;
  return JSON.stringify([
    identity?.name ?? '',
    identity?.logo?.id ?? '',
    identity?.logoNegative?.id ?? '',
    ...campos.map((campo) => identity?.[campo] ?? ''),
  ]);
}

/**
 * LA IDENTIDAD de un pedido: dos pedidos con la misma clave dibujan lo mismo.
 *
 * Sirve para las dos cosas que hacen que la vista previa no mienta: no repetir
 * un pedido que ya se hizo, y descartar una respuesta que llega cuando la
 * pantalla ya está mostrando otra cosa. Sin esto, escribir rápido dejaba en
 * pantalla el PDF de un texto anterior —el que tardó más en volver—.
 */
export function voucherPreviewKey(body: VoucherPreviewBody, identityStamp = ''): string {
  const bloques = body.blocks
    .map((block) => `${block.scope}:${block.key}:${block.lang}:${block.mode ?? ''}:${block.body}`)
    .join('|');
  return `${voucherPreviewSelection(body)}|${identityStamp}|${bloques}`;
}

/**
 * POR QUÉ no se pide la vista previa, o `null` si se puede pedir. El orden es
 * el de lo que hay que arreglar primero.
 */
export function voucherPreviewSkipReason(context: {
  /** La configuración ya se leyó: sin eso no hay ni agencia ni topes. */
  loaded: boolean;
  /** La pantalla ya detectó problemas: el endpoint los rechazaría igual. */
  hasIssues: boolean;
  /** Verticales que ya respondieron que no tienen documento. */
  unsupported: string[];
  vertical: string;
}): 'notLoaded' | 'hasIssues' | 'unsupported' | null {
  if (!context.loaded) return 'notLoaded';
  // Un texto que la pantalla ya marca como inválido vuelve como 400: se ahorra
  // el viaje y se deja en pantalla el último PDF bueno.
  //
  // Es un booleano y no el objeto de problemas a propósito: el objeto se rehace
  // en cada render, y como dependencia de un efecto lo dispararía siempre.
  if (context.hasIssues) return 'hasIssues';
  // Un vertical sin documento no cambia de opinión mientras no cambie de
  // vertical: preguntarlo con cada tecla es puro ruido.
  if (context.unsupported.includes(context.vertical)) return 'unsupported';
  return null;
}

/**
 * QUÉ DECIR cuando no hay PDF. El vocabulario de motivos es el del documento
 * real y sus textos ya existen (`voucherReasonKey`): traducirlos de nuevo acá
 * sería una segunda versión de las mismas frases, que se desincronizan.
 *
 * Con dos excepciones que sí son de esta pantalla:
 * - el logo, porque acá se sube y el mensaje puede decir dónde;
 * - un motivo que el backend agregue y esta versión no conozca, que cae en una
 *   frase genérica en vez de mostrar la clave cruda.
 */
export function voucherPreviewMessageKey(
  result: VoucherPreviewResult | { status: 'error' } | undefined,
): string | null {
  if (!result || result.status === 'ready') return null;
  if (result.status === 'error') return `${KEY}.preview.failed`;
  // La alternativa, cuando la hay, va ADEMÁS de decir que no hay voucher
  // (`voucherPreviewView`), no en su lugar: la pantalla de la reserva las
  // muestra juntas, y sola la alternativa no dice que este producto no emite.
  if (result.status === 'unsupported') return `${KEY}.preview.unsupported`;
  if (result.reason === 'agency_brand_missing') return `${KEY}.preview.needsLogo`;
  return (VOUCHER_NOT_READY_REASONS as readonly string[]).includes(result.reason)
    ? voucherReasonKey(result.reason as VoucherNotReadyReason)
    : `${KEY}.preview.notReady`;
}

/**
 * LA SELECCIÓN que se está mirando: agencia, tipo de producto, proveedor e
 * idioma. Es lo que decide si un dibujo que ya está en pantalla SIGUE
 * correspondiendo.
 *
 * No incluye los textos a propósito: mientras se escribe, el documento anterior
 * —el de hace dos teclas— sigue siendo de esta selección y dejarlo es mejor que
 * un recuadro vacío. Cambiar de vertical o de idioma, en cambio, lo convierte
 * en el documento de otra cosa.
 */
export function voucherPreviewSelection(body: VoucherPreviewBody): string {
  return `${body.agencyId}|${body.vertical}|${body.provider ?? ''}|${body.lang}`;
}

/**
 * ¿HAY QUE PEDIR este dibujo? Solo se saltea el pedido cuando ya hay una
 * respuesta que lo va a contestar: uno viajando que todavía se puede aceptar,
 * o uno que ya se aplicó.
 *
 * La distinción no es teórica. Recordando "la última clave pedida" a secas,
 * esta secuencia dejaba la vista previa muerta: se pide el borrador A, se
 * escribe algo inválido —lo que invalida la respuesta de A, que llega y se
 * descarta—, y al deshacer el error el borrador vuelve a ser A… que ya figura
 * como pedido. Nadie vuelve a pedirlo y la columna queda con el documento
 * viejo, sin rueda y sin explicación.
 */
export function voucherPreviewShouldRequest(context: {
  /** El dibujo que se quiere. */
  key: string;
  /** El que está viajando y todavía se puede aceptar, si hay alguno. */
  inFlight?: string;
  /** El último que se llegó a aplicar en pantalla, si hubo alguno. */
  applied?: string;
}): boolean {
  if (context.inFlight === context.key) return false;
  if (context.applied === context.key) return false;
  return true;
}

/**
 * ¿ES un fallo que se puede reintentar tal cual? Un 400 por un bloque no lo es
 * —el mismo cuerpo va a volver a fallar y el problema ya se muestra en su
 * fila—, pero una red caída o un 500 sí: el mismo borrador, un rato después,
 * puede dibujarse perfecto.
 */
export function voucherPreviewCanRetry(
  result: VoucherPreviewResult | { status: 'error' } | undefined,
): boolean {
  return result?.status === 'error';
}

/** Lo que muestra la columna, decidido en un solo lugar. */
export interface VoucherPreviewView {
  /** Si el documento que hay dibujado corresponde a lo que se está mirando. */
  showDocument: boolean;
  loading: boolean;
  messageKey: string | null;
  /** Lo que SÍ hay para ese producto, cuando no hay voucher. */
  alternativeKey?: string;
}

/**
 * QUÉ MUESTRA la columna. Vive acá y no en el hook porque es una decisión, y
 * las decisiones de esta pantalla se prueban sin DOM.
 *
 * Las dos reglas que esto sostiene:
 *
 * - **Nunca un documento de otra selección.** Un vertical sin voucher dejaba en
 *   pantalla el PDF del vertical anterior, con el cartel de "todavía no hay
 *   voucher" encima.
 * - **La rueda gira solo si hay algo en vuelo.** Cambiar a un vertical sin
 *   voucher mientras un pedido viajaba dejaba la rueda girando para siempre.
 */
export function voucherPreviewView(context: {
  skip: 'notLoaded' | 'hasIssues' | 'unsupported' | null;
  /** Hay un pedido viajando AHORA. */
  inFlight: boolean;
  /** La selección del dibujo que está en pantalla, si hay uno. */
  drawnFor?: string;
  /** La selección que se está mirando. */
  selection: string;
  result?: VoucherPreviewResult | { status: 'error' };
  vertical: string;
}): VoucherPreviewView {
  const propio = Boolean(context.drawnFor) && context.drawnFor === context.selection;
  if (context.skip === 'notLoaded') {
    return { showDocument: false, loading: false, messageKey: null };
  }
  if (context.skip === 'hasIssues') {
    // El dibujo de esta misma selección sigue valiendo: es lo último bueno.
    return { showDocument: propio, loading: false, messageKey: `${KEY}.preview.hasIssues` };
  }
  if (context.skip === 'unsupported') {
    return {
      showDocument: false,
      loading: false,
      messageKey: voucherPreviewMessageKey({ status: 'unsupported' }),
      ...alternativa(context.vertical),
    };
  }
  return {
    showDocument: propio,
    loading: context.inFlight,
    messageKey: voucherPreviewMessageKey(context.result),
    ...(context.result?.status === 'unsupported' ? alternativa(context.vertical) : {}),
  };
}

/** Lo que sí existe para ese producto, cuando no emite voucher. */
function alternativa(vertical: string): { alternativeKey?: string } {
  const key = voucherUnsupportedAlternativeKey(vertical);
  return key ? { alternativeKey: key } : {};
}

/**
 * La respuesta cruda del endpoint, a resultado. El `POST` contesta un PDF o un
 * JSON por el MISMO camino, así que el tipo del cuerpo es lo único que los
 * distingue: pedirlo como blob y mirar el `type` es lo que evita dibujar un
 * `{"status":"unsupported"}` adentro del visor de PDF.
 */
export function voucherPreviewOutcome(params: {
  contentType: string | undefined;
  blob: Blob;
  json: { status?: string; reason?: string } | undefined;
}): VoucherPreviewResult {
  if ((params.contentType ?? '').includes('application/pdf')) {
    return { status: 'ready', blob: params.blob };
  }
  if (params.json?.status === 'unsupported') return { status: 'unsupported' };
  if (params.json?.status === 'not_ready') {
    return { status: 'not_ready', reason: params.json.reason ?? '' };
  }
  // Un 200 que no es ni PDF ni un estado conocido no se dibuja: sería un visor
  // en blanco sin explicación.
  return { status: 'not_ready', reason: '' };
}
